import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { HandHeart, DollarSign, GraduationCap, Handshake, Sparkles, Pencil, Trash2, Plus } from "lucide-react";

interface Props {
  ideaId: string;
  ownerId: string;
}

const KIND_META: Record<string, { label: string; icon: any; blurb: string }> = {
  volunteer: { label: "Volunteer", icon: HandHeart, blurb: "Lend time, skills, or hands-on help." },
  fund: { label: "Fund", icon: DollarSign, blurb: "Sponsor, donate, or invest in this work." },
  mentor: { label: "Mentor", icon: GraduationCap, blurb: "Share expertise, feedback, or guidance." },
  partner: { label: "Partner", icon: Handshake, blurb: "Collaborate as an org, agency, or coalition." },
  other: { label: "Other", icon: Sparkles, blurb: "Have another way to help? Reach out." },
};
const KIND_ORDER = ["volunteer", "fund", "mentor", "partner", "other"] as const;

export default function IdeaAsksTab({ ideaId, ownerId }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isOwner = user?.id === ownerId;
  const [editing, setEditing] = useState<any | null>(null);
  const [editKind, setEditKind] = useState<string>("volunteer");
  const [editHeadline, setEditHeadline] = useState("");
  const [editDetails, setEditDetails] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [respondTo, setRespondTo] = useState<any | null>(null);

  const { data: asks, isLoading } = useQuery({
    queryKey: ["idea-asks", ideaId, isOwner],
    queryFn: async () => {
      let q = supabase.from("idea_asks").select("*").eq("idea_id", ideaId);
      if (!isOwner) q = q.eq("enabled", true);
      const { data, error } = await q.order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const openNew = () => {
    setEditing(null);
    const used = new Set((asks ?? []).map((a: any) => a.kind));
    const next = KIND_ORDER.find((k) => !used.has(k)) ?? "other";
    setEditKind(next);
    setEditHeadline("");
    setEditDetails("");
    setEditOpen(true);
  };

  const openEdit = (a: any) => {
    setEditing(a);
    setEditKind(a.kind);
    setEditHeadline(a.headline);
    setEditDetails(a.details ?? "");
    setEditOpen(true);
  };

  const saveAsk = async () => {
    if (!editHeadline.trim()) return;
    const payload = {
      idea_id: ideaId,
      kind: editKind,
      headline: editHeadline.trim(),
      details: editDetails.trim() || null,
    };
    if (editing) {
      const { error } = await supabase
        .from("idea_asks")
        .update(payload)
        .eq("id", editing.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("idea_asks").insert(payload);
      if (error) return toast.error(error.message);
    }
    toast.success("Saved");
    setEditOpen(false);
    qc.invalidateQueries({ queryKey: ["idea-asks", ideaId] });
  };

  const toggleEnabled = async (a: any) => {
    const { error } = await supabase
      .from("idea_asks")
      .update({ enabled: !a.enabled })
      .eq("id", a.id);
    if (error) toast.error(error.message);
    qc.invalidateQueries({ queryKey: ["idea-asks", ideaId] });
  };

  const removeAsk = async (id: string) => {
    const { error } = await supabase.from("idea_asks").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Ask removed");
    qc.invalidateQueries({ queryKey: ["idea-asks", ideaId] });
  };

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;

  const usedKinds = new Set((asks ?? []).map((a: any) => a.kind));
  const canAddMore = KIND_ORDER.some((k) => !usedKinds.has(k));

  return (
    <div className="space-y-4">
      {isOwner && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Tell supporters how they can help. Responses go to your inbox.
          </p>
          {canAddMore && (
            <Button onClick={openNew} size="sm" className="gap-2">
              <Plus className="h-4 w-4" /> Add ask
            </Button>
          )}
        </div>
      )}

      {(!asks || asks.length === 0) && (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          {isOwner
            ? "No asks yet. Add one so partners and funders know how to plug in."
            : "The owner hasn't listed specific asks yet."}
        </Card>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {(asks ?? []).map((a: any) => {
          const meta = KIND_META[a.kind] ?? KIND_META.other;
          const Icon = meta.icon;
          return (
            <Card key={a.id} className="p-5 flex flex-col">
              <div className="flex items-start justify-between gap-2 mb-2">
                <Badge className="gap-1 bg-primary/15 text-primary border border-primary/30">
                  <Icon className="h-3 w-3" /> {meta.label}
                </Badge>
                {isOwner && (
                  <div className="flex items-center gap-1">
                    <Switch checked={a.enabled} onCheckedChange={() => toggleEnabled(a)} />
                    <Button variant="ghost" size="icon" onClick={() => openEdit(a)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => removeAsk(a.id)}>
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </div>
                )}
              </div>
              <h4 className="font-semibold text-foreground mb-1">{a.headline}</h4>
              <p className="text-sm text-muted-foreground flex-1 whitespace-pre-wrap">
                {a.details || meta.blurb}
              </p>
              {!isOwner && a.enabled && (
                <Button className="mt-4" size="sm" onClick={() => setRespondTo(a)}>
                  Offer to {meta.label.toLowerCase()}
                </Button>
              )}
              {isOwner && !a.enabled && (
                <p className="mt-3 text-xs text-muted-foreground italic">Hidden from public</p>
              )}
            </Card>
          );
        })}
      </div>

      {/* Owner edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit ask" : "Add an ask"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={editKind} onValueChange={setEditKind} disabled={!!editing}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {KIND_ORDER.map((k) => (
                  <SelectItem
                    key={k}
                    value={k}
                    disabled={!editing && usedKinds.has(k)}
                  >
                    {KIND_META[k].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Headline (e.g. Looking for a fiscal sponsor)"
              value={editHeadline}
              onChange={(e) => setEditHeadline(e.target.value)}
              maxLength={140}
            />
            <Textarea
              placeholder="Details — what kind of help, what success looks like, any constraints."
              value={editDetails}
              onChange={(e) => setEditDetails(e.target.value)}
              rows={4}
              maxLength={2000}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveAsk} disabled={!editHeadline.trim()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Responder dialog */}
      <RespondDialog
        ask={respondTo}
        ideaId={ideaId}
        onClose={() => setRespondTo(null)}
      />
    </div>
  );
}

function RespondDialog({
  ask,
  ideaId,
  onClose,
}: {
  ask: any | null;
  ideaId: string;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!ask) return;
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Name, email, and message are required");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("idea_ask_responses").insert({
      ask_id: ask.id,
      idea_id: ideaId,
      responder_user_id: user?.id ?? null,
      name: name.trim(),
      email: email.trim(),
      organization: org.trim() || null,
      message: message.trim(),
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success("Sent — the project owner has been notified");
    setName(""); setEmail(""); setOrg(""); setMessage("");
    onClose();
  };

  return (
    <Dialog open={!!ask} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {ask ? `Reach out: ${KIND_META[ask.kind]?.label ?? "Help"}` : ""}
          </DialogTitle>
        </DialogHeader>
        {ask && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{ask.headline}</p>
            <Input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
            <Input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={320} />
            <Input placeholder="Organization (optional)" value={org} onChange={(e) => setOrg(e.target.value)} maxLength={200} />
            <Textarea
              placeholder="How would you like to help? Share context, links, or a quick intro."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={4000}
            />
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>{submitting ? "Sending..." : "Send"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}